import { Column, Row } from 'app/design/primitives/box';
import { Input } from 'app/design/primitives/input';
import { OpenSettingsPrompt } from 'app/components/open-settings-prompt';
import { useTutorialRef } from 'app/components/tutorial-overlay';
import { ImagePickerIcon } from 'app/design/icons/imagePicker';
import { useTheme } from '@shopify/restyle';

import { TutorialRef } from 'app/types/tutorial';
import { THUMBS_UP } from 'app/network/chat/message';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable } from 'react-native';
import type { ChannelToolbarProps } from './channel-toolbar.types';
import { useChannelToolbarComposer } from './use-channel-toolbar-composer';
import { Text } from 'app/design/primitives/text';
import type { Theme } from 'app/shopify';
import { IconCamera, IconSend } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH, isMobileNative } from 'app/utils/constants';
import type { TFunction } from 'i18next';
export type { ChannelToolbarProps } from './channel-toolbar.types';
export { useChannelToolbar } from './use-channel-toolbar';

const ToolbarMediaActions = ({
  actionsDisabled,
  buttonSize,
  iconColor,
  iconDisabledColor,
  iconSize,
  onUseImagePickerCamera,
  t,
}: {
  actionsDisabled: boolean;
  buttonSize: number;
  iconColor: string;
  iconDisabledColor: string;
  iconSize: number;
  onUseImagePickerCamera: (source: { camera?: boolean; library?: boolean }) => void;
  t: TFunction;
}) => (
  <Row gap="spacing.general.x1">
    {isMobileNative ? (
      <Pressable
        accessibilityLabel={t('chat.toolbar.openCamera', { ns: 'accessibility' })}
        accessibilityRole="button"
        disabled={actionsDisabled}
        onPress={() => onUseImagePickerCamera({ camera: true })}
        style={{ width: buttonSize, height: buttonSize, alignItems: 'center', justifyContent: 'center' }}
      >
        <IconCamera
          strokeWidth={ICON_STROKE_WIDTH}
          color={actionsDisabled ? iconDisabledColor : iconColor}
          size={iconSize}
        />
      </Pressable>
    ) : null}
    <Pressable
      accessibilityLabel={t('chat.toolbar.openLibrary', { ns: 'accessibility' })}
      accessibilityRole="button"
      disabled={actionsDisabled}
      onPress={() => onUseImagePickerCamera({ library: true })}
      style={{ width: buttonSize, height: buttonSize, alignItems: 'center', justifyContent: 'center' }}
    >
      <ImagePickerIcon color={actionsDisabled ? iconDisabledColor : iconColor} height={iconSize} width={iconSize} />
    </Pressable>
  </Row>
);

const ToolbarSendAction = ({
  actionsDisabled,
  buttonSize,
  iconColor,
  iconDisabledColor,
  iconSize,
  onLikePress,
  onSendPress,
  textContent,
  t,
}: {
  actionsDisabled: boolean;
  buttonSize: number;
  iconColor: string;
  iconDisabledColor: string;
  iconSize: number;
  onLikePress: () => void;
  onSendPress: () => void;
  textContent: string;
  t: TFunction;
}) => {
  const theme = useTheme();
  if (textContent.length > 0) {
    return (
      <Pressable
        accessibilityLabel={t('chat.toolbar.sendMessage', { ns: 'accessibility' })}
        accessibilityRole="button"
        disabled={actionsDisabled}
        onPress={onSendPress}
        style={{ width: buttonSize, height: buttonSize, alignItems: 'center', justifyContent: 'center' }}
      >
        <IconSend
          strokeWidth={ICON_STROKE_WIDTH}
          color={actionsDisabled ? iconDisabledColor : iconColor}
          size={iconSize}
        />
      </Pressable>
    );
  }
  return (
    <Pressable
      accessibilityLabel={t('chat.toolbar.sendLike', { ns: 'accessibility' })}
      accessibilityRole="button"
      disabled={actionsDisabled}
      onPress={onLikePress}
      style={{
        width: buttonSize,
        height: buttonSize,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: -theme.spacing['spacing.general.x2'],
      }}
    >
      <Text fontSize={20} style={{ color: actionsDisabled ? iconDisabledColor : iconColor }}>
        {THUMBS_UP}
      </Text>
    </Pressable>
  );
};

export function ChannelToolbar(props: ChannelToolbarProps) {
  const { disabled = false, textContent, setTextContent } = props;

  const { onUseImagePickerCamera, settingsPromptState, onLikePress, onSendPress, onKeyPress, isActionsDisabled } =
    useChannelToolbarComposer(props);

  const { t } = useTranslation();
  const { viewRef: chatInputFieldRef } = useTutorialRef(TutorialRef.ChatInputField);
  const theme = useTheme<Theme>();
  const iconColor = theme.colors['text.primary'];
  const iconDisabledColor = theme.colors['surface.disabled'];
  const buttonSize = 40;
  const iconSize = 36;
  const actionsDisabled = disabled || isActionsDisabled;

  return (
    <Column
      style={{
        paddingHorizontal: 12,
        paddingTop: 8,
        paddingBottom: Platform.OS === 'ios' ? 16 : 8,
        borderTopWidth: 1,
        borderTopColor: theme.colors['surface.disabled'],
        backgroundColor: theme.colors['surface.default'],
        maxHeight: 100,
      }}
    >
      <Row alignItems="center" collapsable={false} gap="spacing.general.x2" width="100%">
        <ToolbarMediaActions
          {...{
            actionsDisabled,
            buttonSize,
            iconColor,
            iconDisabledColor,
            iconSize,
            onUseImagePickerCamera,
            t,
          }}
        />

        <Row flex={1} collapsable={false} innerRef={chatInputFieldRef}>
          <Input
            editable={!disabled && !isActionsDisabled}
            multiline
            onChangeText={setTextContent}
            onKeyPress={onKeyPress}
            placeholder={t('toolbar.placeHolder', { ns: 'one-channel' })}
            right={
              <ToolbarSendAction
                {...{
                  actionsDisabled,
                  buttonSize,
                  iconColor,
                  iconDisabledColor,
                  iconSize,
                  onLikePress,
                  onSendPress,
                  t,
                  textContent,
                }}
              />
            }
            value={textContent}
          />
        </Row>
      </Row>
      <OpenSettingsPrompt
        isOpen={settingsPromptState.isOpen}
        onClose={settingsPromptState.onClose}
        settingName="camera"
      />
    </Column>
  );
}
